<script setup lang="ts">
import type { CommonLocaleSelectorProperties, Language } from '@optionway/models';
import { ChevronDown } from 'lucide-vue-next';

const properties = defineProps<CommonLocaleSelectorProperties>();

const prefix = 'components.common.localeSelector';

const { $appConfig } = useNuxtApp();
const config = $appConfig.app?.components?.commonLocaleSelector;

const { locales, locale, setLocale } = useI18n();
const gtmStore = useGtmStore();
const availableLocales: Language[] = [];
for (const current of locales.value) {
  availableLocales.push(current.code);
}

const componentConfig = computed<CommonLocaleSelectorProperties>(() => {
  return {
    languages:
      properties.languages?.map((lang) => lang.toLowerCase() as Language) ??
      config?.languages?.map((lang) => lang.toLowerCase() as Language) ??
      availableLocales,
  };
});

const allowedLocalesNotSelected = computed(() => {
  return locales.value.filter(
    (index) => index.code !== locale.value && componentConfig.value.languages?.includes(index.code),
  );
});

const allowedLocales = computed(() => {
  return locales.value.filter((index) => componentConfig.value.languages?.includes(index.code));
});

const mobileAccordionValue = ref('');
const isDesktopOpen = ref(false);

const selectLocale = async (code: Language) => {
  gtmStore.trackLocaleChanged(locale.value, code);
  await setLocale(code);
  mobileAccordionValue.value = '';
  isDesktopOpen.value = false;
};

if (import.meta.test) {
  defineExpose({ allowedLocalesNotSelected, availableLocales });
}
</script>

<template>
  <!-- Desktop: Popover dropdown (click-based) -->
  <div class="hidden lg:block">
    <UiPopover
      v-if="allowedLocales.length > 0"
      v-model:open="isDesktopOpen"
    >
      <UiPopoverTrigger
        :aria-label="$t(`${prefix}.label`)"
        class="rounded-md py-2.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary-700"
      >
        <div class="flex items-center gap-1.5">
          <component
            :is="`SvgoCommonLocaleSelectorFlag${useCapitalize(locale)}`"
            filled
            aria-hidden="true"
          />
          <ChevronDown
            class="size-4 text-text-secondary-600 transition-transform duration-200"
            :class="{ 'rotate-180': isDesktopOpen }"
            aria-hidden="true"
          />
        </div>
      </UiPopoverTrigger>
      <UiPopoverContent
        align="start"
        :side-offset="-6"
        class="w-48 rounded-md p-1"
      >
        <div class="flex flex-col gap-1">
          <button
            v-for="lang in allowedLocales"
            :key="lang.code"
            :aria-current="lang.code === locale ? 'true' : undefined"
            class="flex w-full cursor-pointer select-none items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:ring-2 focus-visible:ring-brand-primary-700"
            :class="{ 'bg-brand-primary-50': lang.code === locale }"
            @click="selectLocale(lang.code)"
          >
            <div class="flex items-center gap-2">
              <component
                :is="`SvgoCommonLocaleSelectorFlag${useCapitalize(lang.code)}`"
                filled
                aria-hidden="true"
              />
              <span :class="{ 'font-semibold text-text-primary-900': lang.code === locale }">
                {{ $t(`${prefix}.${lang.code}`) }}
              </span>
            </div>
            <SvgoCommonLocaleSelectorCheck
              v-if="lang.code === locale"
              filled
              aria-hidden="true"
              class="size-4 text-brand-primary-700"
            />
          </button>
        </div>
      </UiPopoverContent>
    </UiPopover>
  </div>

  <!-- Mobile: Accordion inline in burger menu -->
  <div class="lg:hidden">
    <template v-if="allowedLocales.length > 1">
      <UiAccordion
        v-model="mobileAccordionValue"
        type="single"
        collapsible
      >
        <UiAccordionItem
          value="language"
          class="border-0"
        >
          <UiAccordionTrigger
            class="rounded-lg px-3 py-2.5 text-sm font-normal text-text-secondary-600 hover:bg-accent"
          >
            <div class="flex flex-1 items-center justify-between pr-2">
              <span>{{ $t(`${prefix}.label`) }}</span>
              <component
                :is="`SvgoCommonLocaleSelectorFlag${useCapitalize(locale)}`"
                filled
                class="size-4"
              />
            </div>
          </UiAccordionTrigger>
          <UiAccordionContent class="p-0">
            <div class="flex flex-col gap-1 px-2 py-1">
              <template
                v-for="lang in allowedLocales"
                :key="lang.code"
              >
                <UiButton
                  variant="tertiary"
                  color="gray"
                  size="xs"
                  :aria-current="lang.code === locale ? 'true' : undefined"
                  class="flex w-full items-center justify-between rounded-lg px-4 py-2.5 text-sm font-normal text-text-secondary-600"
                  :class="{ 'bg-brand-primary-50 font-semibold text-text-primary-900': lang.code === locale }"
                  @click="selectLocale(lang.code)"
                >
                  <div class="flex items-center gap-3">
                    <component
                      :is="`SvgoCommonLocaleSelectorFlag${useCapitalize(lang.code)}`"
                      filled
                      aria-hidden="true"
                    />
                    <span>{{ $t(`${prefix}.${lang.code}`) }}</span>
                  </div>
                  <SvgoCommonLocaleSelectorCheck
                    v-if="lang.code === locale"
                    filled
                    aria-hidden="true"
                    class="text-brand-primary-700"
                  />
                </UiButton>
              </template>
            </div>
          </UiAccordionContent>
        </UiAccordionItem>
      </UiAccordion>
    </template>
    <div
      v-else
      class="flex justify-between px-3 py-2.5 text-sm font-normal text-text-secondary-600"
    >
      <span>{{ $t(`${prefix}.label`) }}</span>
      <component
        :is="`SvgoCommonLocaleSelectorFlag${useCapitalize(locale)}`"
        filled
        class="size-4"
      />
    </div>
  </div>
</template>
